import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';

export interface InventoryBuildingOption {
  code?: string | null;
  id: string;
  name: string;
}

export interface InventoryFloorplan {
  floor: string;
  height?: number;
  id: string;
  name: string;
  width?: number;
}

interface StrapiEntity {
  floor?: string;
  height?: number;
  id?: number | string;
  name?: string;
  width?: number;
}

export class NewMeterLocationClient {
  private readonly axios: InventoryAxiosClient;

  constructor(input: { axiosClient?: InventoryAxiosClient } = {}) {
    this.axios = input.axiosClient ?? createInventoryAxiosClient();
  }

  async findFloorplan(input: {
    buildingId: string | null;
    floor: string;
    locationApiKey: string;
    locationId: number;
  }): Promise<InventoryFloorplan | null> {
    const axios = this.createLocationAxiosClient(input.locationApiKey);
    const response = await axios.get<{ data: StrapiEntity[] }>('/inventory-location-building-floorplans', {
      params: {
        'filters[floor][$eq]': input.floor,
        ...(input.buildingId
          ? { 'filters[inv_loc_building][id][$eq]': input.buildingId }
          : { 'filters[inv_location][id][$eq]': input.locationId }),
        'pagination[pageSize]': 1,
        'populate[inv_loc_building]': true,
        'populate[inv_location]': true,
      },
    });
    const entity = response.data.data[0];

    if (!entity) {
      return null;
    }

    const id = String(entity.id ?? '');

    return {
      floor: entity.floor ?? input.floor,
      height: entity.height,
      id,
      name: entity.name ?? '',
      width: entity.width,
    };
  }

  async getFloorplanContent(input: { floorplanId: string; locationApiKey: string }): Promise<Blob> {
    const axios = this.createLocationAxiosClient(input.locationApiKey);
    const response = await axios.get<Blob>(`/inventory-location-building-floorplans/${input.floorplanId}/content`, {
      responseType: 'blob',
    });

    return response.data;
  }

  private createLocationAxiosClient(locationApiKey: string) {
    return createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey,
    });
  }
}
